import { startOfToday } from 'date-fns'
import { type Dispatch, type SetStateAction, useRef } from 'react'
import { toast } from 'sonner'

import { Banner } from '@/components/ui/banner'
import { Card, CardContent } from '@/components/ui/card'
import { DateInput } from '@/components/ui/DateInput'
import { parseError } from '@/lib/errors'
import { formatDate, parseDateOnly } from '@/lib/formatters'
import {
  EffectiveBasis,
  type PolicyChangeLine,
  type PolicyChangeRequest,
  policyChangeWrites,
  usePolicyChangeMutation,
} from '@/models/policyChange'
import { BASIS_LABELS, changeLines, KIND_LABELS, toIsoDate } from './policyChangeCopy'

function datesGiven(line: PolicyChangeLine): string {
  return [
    line.termLetterDate && `Termination letter ${formatDate(line.termLetterDate)}`,
    line.lastLoadDate && `Last load ${formatDate(line.lastLoadDate)}`,
    line.startDate && `Starts ${formatDate(line.startDate)}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

interface ChangeDatesProps {
  orgId: string
  request: PolicyChangeRequest
  /** Counts the saves queued or in flight, so the page can hold its buttons until they finish. */
  setPendingSaves: Dispatch<SetStateAction<number>>
}

/** The date each change starts: suggested from the customer's dates, never before today. */
export function ChangeDates({ orgId, request, setPendingSaves }: ChangeDatesProps) {
  const updateChange = usePolicyChangeMutation(orgId, policyChangeWrites.updateChange)
  const lines = request.changes.filter((line) => !line.dropped)
  const moved = lines.some((line) => line.effectiveBasis === EffectiveBasis.BACKDATE_LIMIT)

  const saves = useRef(Promise.resolve())

  function choose(line: PolicyChangeLine, date: Date | undefined) {
    setPendingSaves((count) => count + 1)
    // One at a time, so a quick correction can't be overwritten by the earlier save finishing last.
    saves.current = saves.current
      .then(() =>
        updateChange.mutateAsync({
          requestId: request.id,
          changeId: line.id,
          change: { requestedDate: toIsoDate(date) },
        })
      )
      .then(
        () => undefined,
        (error: unknown) => {
          toast.error(parseError(error))
        }
      )
      .finally(() => setPendingSaves((count) => count - 1))
  }

  return (
    <>
      {moved && (
        <Banner variant="warning" small>
          A change can’t start before today, so changes dated earlier start on{' '}
          {formatDate(request.earliestEffectiveDate)}.
        </Banner>
      )}
      <Card>
        <CardContent className="flex flex-col divide-y divide-neutral-100">
          {lines.map((line) => (
            <div
              key={line.id}
              className="grid grid-cols-1 items-center gap-3 py-3 sm:grid-cols-[2fr_1fr_1fr]"
            >
              <div className="text-sm">
                <div className="font-medium text-neutral-900">{KIND_LABELS[line.kind]}</div>
                <div className="text-neutral-600">{changeLines(line).slice(0, 2).join(' · ')}</div>
                {datesGiven(line) && <div className="text-xs text-neutral-500">{datesGiven(line)}</div>}
              </div>
              <DateInput
                value={parseDateOnly(line.requestedDate ?? line.effectiveDate)}
                onChange={(date) => choose(line, date)}
                hidden={{ before: startOfToday() }}
                inputProps={{ 'aria-label': `Start date for ${KIND_LABELS[line.kind]}` }}
              />
              <div className="text-xs text-neutral-600">
                {line.effectiveBasis && BASIS_LABELS[line.effectiveBasis]}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  )
}
